import {
    Component,
    ContentChild,
    ViewChild,
    ViewContainerRef,
    ComponentRef,
    ContentChildren,
    QueryList, AfterViewInit, OnDestroy,
} from '@angular/core';
import {from, forkJoin} from 'rxjs';
import {DefaultViewDirective} from './default-view.directive';
import {FallbackViewDirective} from './fallback-view.directive';
import {ErrorViewDirective} from './error-view.directive';
import {InjectionToken, Type} from '@angular/core';
import {ObservableInput} from 'rxjs';

export const SUSPENSE = new InjectionToken('SUSPENSE');

export interface Suspenseable {
    setup$(): ObservableInput<any>;
}

export const useSuspense = (comp: Type<Suspenseable>) => ({
    provide: SUSPENSE,
    useExisting: comp,
});

@Component({
    selector: 'suspense',
    template: `
        <ng-template #anchor></ng-template>
        @if (show) {
          <ng-content></ng-content>
        }
        `,
})
export class SuspenseComponent implements AfterViewInit, OnDestroy {
    @ViewChild('anchor', {read: ViewContainerRef}) anchor: ViewContainerRef;
    @ContentChild(DefaultViewDirective) defaultView: DefaultViewDirective;
    @ContentChild(FallbackViewDirective) fallbackView: FallbackViewDirective;
    @ContentChild(ErrorViewDirective) errorView: ErrorViewDirective;
    @ContentChildren(SUSPENSE as any) suspenseables: QueryList<Suspenseable>;

    show = false;
    private compRef: ComponentRef<Suspenseable>;

    constructor(private container: ViewContainerRef) {
    }

    ngAfterViewInit() {
        this.anchor.createEmbeddedView(this.fallbackView.tpl);
        const isLazy = this.defaultView?.fetch;

        /* static load of components */
        if (!isLazy) {
            const setup$ = this.suspenseables.map((comp) => comp.setup$());
            forkJoin(setup$).subscribe({
                next: () => {
                    this.anchor.clear();
                    this.show = true;
                },      
                error: () => {
                    this.anchor.remove();
                    this.anchor.createEmbeddedView(this.errorView.tpl);
                },
            });
            return;
        }

        /* dynamic load of components */
        this.defaultView.fetch().then((comp) => {
            this.compRef = this.container.createComponent(comp.default);

            from(this.compRef.instance.setup$()).subscribe({
                next: () => {
                    this.anchor.remove();
                    this.anchor.insert(this.compRef.hostView);
                },
                error: () => {
                    this.anchor.remove();
                    this.anchor.createEmbeddedView(this.errorView.tpl);
                },
            });
        });
    }

    ngOnDestroy() {
        this.compRef?.destroy();
        this.compRef = null;
    }
}
